<template>
  <tile class="opc-zpi">
    <foldable v-model="isExpanded" :title="title">
      <div slot="head" class="opc-zm-base-card__head">
        <div class="opc-zm-base-card__head-left">
          <img :src="logo" class="opc-zm-base-card__logo" :alt="title + ' Icon'" tabindex="-1" />
        </div>
        <div class="opc-zm-base-card__head-right">
          <h2 class="opc-zm-base-card__name text-capitalize">
            {{ title }}
            <i class="zm-icon-success main-icon" v-if="!isExpanded" aria-hidden="true"></i>
          </h2>
          <div v-if="!isExpanded" slot="collapse" class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
            <ul>
              <li v-if="selectZPI">
                {{ $t('billing.pbx.cart_summary.add_on_lines', [selectZPI.cartInfo.selected_number,
                `${$money(amount)}${unit}`]) }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="opc-zpi__main">
        <zm-form>
          <p class="opc-zpi__desc">
            {{ $t('billing.pbx.plan_desc.ZPI') }}
            <zm-tooltip class="opc-zpi__desc-countries" placement="top" v-model="supportCountryVisible" aria-label="learn more about countries/territory"
              :content="$t('billing.pbx.intl_select_addon_countries')">
              <zm-button data-testid="checkout-button-241" type="link">
                {{ $t('billing.pbx.see_supported_country', [19]) }}
              </zm-button>
            </zm-tooltip>
          </p>
          <zm-form-item class="horizontal-form-item">
            <InputNumberWithPopover v-model="form.number" :min="minHost" :max="99" :decrease-tips="decreaseTips" @change="handleNumberChange" label="International Calling Add-On Select the number of users"
              :disabled="disabled" size="large" controls-position="both" />
            <template>
              <span v-if="$context.is.desktop || $context.is.tablet" class="opc-zpi__unit">
                {{ $t('billing.planform.unit.license') }}
                <span v-html="'\u00d7'"></span>
                {{$money(amount) }}{{ unit }}<priceVATTip />
              </span>
              <div v-else class="opc-zpi__unit">
                <p class="opc-zpi__unit--license">{{ $t('billing.planform.unit.license') }}</p>
                <span class="opc-zpi__unit--price-unit">{{ $money(amount) }}{{ unit }}<priceVATTip /></span>
              </div>
            </template>
          </zm-form-item>
        </zm-form>
        <div class="opc-zm-base-card__main__footer">
          <div class="opc-zm-base-card__btn--wrap">
            <zm-tooltip :disabled='!disableDeleteTips' placement='top' tabindex="-1">
              <template #content>
                <div v-html='disableDeleteTips'></div>
              </template>
              <div>
                <zm-button data-testid="checkout-button-242" :disabled='disabled || disableDeleteTips' type='link' ghost @click='removePlanByKey'
                           :aria-label="'remove ' + title + ' plan'">{{ $t('billing.common.remove') }}
                </zm-button>
              </div>
            </zm-tooltip>
          </div>
        </div>
      </div>
    </foldable>
  </tile>
</template>
<script>
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import logo from '@/assets/plans/zoom_phone_international_calling.svg';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import { mapGetters, mapMutations, mapState } from 'vuex';
import { computedCallingPlans } from '@/common/service/computers/plans/pbx';
import { BILLING_CYCLE, contactUsPath, planTypes } from '@/common/constants';
import plansMixin from '@/plans/mx/plan';
import { flowTypes } from '@/common/service/page-config';
import { get } from 'lodash';

export default {
  data() {
    return {
      logo,
      title: this.$t('billing.pbx.plan_name.ZPI'),
      isExpanded: true,
      supportCountryVisible: false,
      form: {
        number: 0,
      },
    };
  },
  mixins: [plansMixin],
  components: {
    Foldable,
    Tile,
    InputNumberWithPopover,
  },
  created() {
    this.initConfigCart();
  },
  computed: {
    ...mapState({
      planMapFlat: (state) => state.ShoppingCart.planMapFlat,
    }),
    ...mapGetters({
      'getSelectedPlans': 'getSelectedPlans',
      'getUserCurrentSubscriptions': 'getUserCurrentSubscriptions',
      'plancodeMapSubscriptionDisabled': 'plancodeMapSubscriptionDisabled',
      'boughtPlans': 'boughtPlans',
      'basicConfig': 'basicConfig',
    }),
    disabled() {
      const planCode = this.selectZPI?.plan_code;
      return !!this.plancodeMapSubscriptionDisabled[planCode];
    },
    allPlans() {
      return this.planMapFlat.filter(item => item.type === planTypes.ZOOM_PHONE$ZPI);
    },
    isStandardPrice() {
      const isEditFlow = this.$context.flow === flowTypes.edit;
      return (isEditFlow && !this.hasAccountDiscount) ? this.boughtZPI?.isStandardPrice : true;
    },
    minHost() {
      if (this.$context.flow === flowTypes.edit && (this.basicConfig.downSellData.disableDownSell || !this.isStandardPrice)) {
        return get(this.boughtZPI, 'cartInfo.selected_number', 1);
      }
      return 1;
    },
    decreaseTips() {
      if (this.selectZPI?.cartInfo?.selected_number <= this.boughtZPI?.cartInfo?.selected_number) {
        if (this.basicConfig.downSellData.disableDownSell) {
          return this.$t('billing.error.can_not_bought_plan_tips');
        }

        return this.forNonStandardPriceTips;
      }

      return '';
    },
    currentBillingCycle() {
      const selectCallingPlan = computedCallingPlans(this.getUserCurrentSubscriptions)[0];
      if (selectCallingPlan) {
        return selectCallingPlan.billing_cycle;
      }
      return BILLING_CYCLE.ANNUAL;
    },
    cycleMap() {
      return {
        [BILLING_CYCLE.MONTHLY]: this.allPlans.find(item => item.billing_cycle === BILLING_CYCLE.MONTHLY),
        [BILLING_CYCLE.ANNUAL]: this.allPlans.find(item => item.billing_cycle === BILLING_CYCLE.ANNUAL),
      }
    },
    boughtZPI() {
      return this.boughtPlans.find((p) => p.plan_code === this.selectZPI?.plan_code);
    },
    selectZPI() {
      return this.getSelectedPlans.find(item => item.type === planTypes.ZOOM_PHONE$ZPI);
    },
    defaultZPI() {
      if (this.selectZPI) {
        return this.selectZPI;
      }
      return this.allPlans.find(item => item.billing_cycle === this.currentBillingCycle) || this.allPlans[0];
    },
    amount() {
      if (!this.defaultZPI) {
        return 0;
      }
      return this.defaultZPI.amount;
    },
    unit() {
      return this.$t(`billing.common.per_billing_cycle.${this.defaultZPI.billing_cycle}`)
    },
    disableDeleteTips() {
      const hasBought = this.boughtPlans.find((p) => p.plan_code === this.selectZPI?.plan_code);
      if ((hasBought && this.basicConfig?.isDirectSales) || hasBought?.isDisablePlanChangeOnline || hasBought?.ratePlans?.length > 1) {
        return this.$t('billing.cancel_disable_contact_us', {
          url: contactUsPath,
        })
      }
      return null;
    },
  },
  methods: {
    ...mapMutations({
      updateSelectPlan: 'updateSelectPlan',
    }),
    initConfigCart() {
      if (this.selectZPI) {
        this.form.number = this.selectZPI.cartInfo.selected_number;
        return;
      }
      this.form.number = 1;
      this.handleNumberChange(1);
    },
    handleNumberChange(number, oldValue) {
      if (oldValue && oldValue > number) {
        this.$tracking.buyInteraction('zoom phone add-ons', `International Calling Add-On - decrease - ${number}`);
      } else if (oldValue && oldValue < number) {
        this.$tracking.buyInteraction('zoom phone add-ons', `International Calling Add-On - increase - ${number}`);
      }
      if (!this.defaultZPI) return;
      this.updateSelectPlan({
        plan_code: this.defaultZPI.plan_code,
        cartInfo: { selected_number: number },
      });
    },
    switchBillingCycle() {
      if (!this.selectZPI) {
        this.initConfigCart()
      }
      const nextZPI = this.cycleMap[this.currentBillingCycle];
      if (!nextZPI) return;
      if (this.selectZPI.plan_code === nextZPI.plan_code) return;
      this.switchSelectPlan([this.selectZPA.plan_code, nextZPI.plan_code, this.selectZPI.cartInfo])
    },
  },
  watch: {
    supportCountryVisible(v) {
      if (v) {
        this.$tracking.buyInteraction('zoom phone add-ons', `International Calling Add-On - see list of supported 19 countries/territory`);
      }
    },
    isExpanded: {
      handler(val) {
        this.$tracking.buyInteraction(
          'product details accordion',
          `Zoom Phone International Calling Add-On - ${val ? 'expand' : 'collapse'}`,
        );
      },
    },
    currentBillingCycle(v, ov) {
      if (v !== ov) {
        this.switchBillingCycle();
      }
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/zoom-card.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-zpi {
  .opc-zm-base-card {
    &__name {
      @include media(mobile) {
        display: block;
        padding-right: 70px;

        .main-icon {
          transform: translateY(-2px);
        }
      }
    }
  }

  .main-icon {
    color: $theme-color;
  }

  &__main {
    margin-top: 18px;
    border-top: none;
  }

  &__name {
    font-family: 'Almaden Sans';
    font-style: normal;
    font-weight: 700;
    font-size: 20px;
    line-height: 150%;
    color: #00053D;
    display: flex;
    flex-direction: row;
    align-items: center;

    .zm-icon-success {
      font-size: 16px;
      margin-left: 12px;
    }
  }

  &__desc {
    font-family: 'Almaden Sans';
    font-style: normal;
    font-weight: 450;
    font-size: 14px;
    line-height: 150%;
    color: #00053D;
    margin-bottom: 16px;

    @include media(mobile) {
      &-countries {
        &.zm-button.zm-button--link {
          text-align: left;
        }
      }
    }
  }

  @include media(tablet, desktop) {
    &__unit {
      margin-left: 8px;
    }
  }

  @include media(mobile) {
    .zm-form-item.horizontal-form-item {
      .zm-form-item__content {
        display: flex;
      }
    }

    &__unit {
      margin-left: 8px;

      &--license {
        color: #00053D;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%; /* 16.8px */

        &:first-letter {
          text-transform: capitalize;
        }
      }

      &--price-unit {
        color: #666487;
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%; /* 14.4px */
      }
    }
  }

  .opc-pbx__desc {

    ul,
    li {
      list-style: none;
    }

    li {
      font-family: 'Almaden Sans';
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 120%;
      color: #00031F;
    }
  }
}</style>
